import React, { useEffect, useState } from "react";

// material-ui

import {
  Box,
  Button,
  DialogActions,
  DialogContent,
  DialogTitle,
  Divider,
  FormControl,
  FormControlLabel,
  Grid,
  FormHelperText,
  InputLabel,
  ListItemText,
  MenuItem,
  OutlinedInput,
  Select,
  SelectChangeEvent,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { LocalizationProvider } from "@mui/x-date-pickers";

// third-party
import _ from "lodash";
import * as Yup from "yup";
import { useFormik, Form, FormikProvider } from "formik";

// project imports
import CircularWithPath from "../../../../../components/@extended/progress/CircularWithPath";

import { openSnackbar } from "../../../../../api/snackbar";

// assets

// types
import { SnackbarProps } from "../../../../../types/snackbar";
import { FormattedMessage, useIntl } from "react-intl";
import { USERAXIOS } from "../../../../../api/userAxios";
import ReusableModal from "../../../../../components/ReusableComponent/ReusableModal";
import { CommentAction } from "../../../../../types/commentAction";

// constant
const getInitialValues = (commentAction: CommentAction | null) => {
  if (commentAction) {
    return {
      value: commentAction.value || "",
      value_trans: commentAction.value_trans.ar || "",
      status: commentAction.status || false,
    };
  } else {
    return {
      value: "",
      status: false,
      value_trans: "",
    };
  }
};
// ==============================|| CATEGORY ADD / EDIT - FORM ||============================== //

const FormCommentActionAdd = ({
  open,
  commentAction,
  id,
  loading,
  fetchData,
  setLoading,
  handleClose,
  setResult,
}: {
  open: boolean;
  loading: boolean;
  id: number;
  setLoading: any;
  fetchData: CommentAction[];
  commentAction: CommentAction | null;
  handleClose: () => void;
  setResult: any;
}) => {
  const intl = useIntl();

  useEffect(() => {
    setLoading(false);
  }, [id, commentAction]);

  const CommentActionSchema = Yup.object().shape({
    value: Yup.string().required("English name is required"),
    value_trans: Yup.string().required("Arabic name is required"),
  });

  const formik = useFormik({
    initialValues: getInitialValues(commentAction!),
    validationSchema: CommentActionSchema,
    enableReinitialize: true,
    onSubmit: async (values, { setSubmitting }) => {
      try {
        const commentActionData = {
          value: values.value,
          status: values.status,
          value_trans: {
            ar: values.value_trans,
          },
        };

        if (commentAction) {
          const response = await USERAXIOS.post(
            `/comment_actions/${commentAction.id}`,
            commentActionData
          );
          const updatedCommentAction = response.data.data;

          if (updatedCommentAction) {
            setResult((prev) =>
              prev.map((cat) =>
                cat.id === updatedCommentAction.id ? updatedCommentAction : cat
              )
            );

            openSnackbar({
              open: true,
              message: "Comment Action Edited Successfully",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        } else {
          const response = await USERAXIOS.post(
            `/comment_actions`,
            commentActionData
          );

          const newCommentAction = response.data.data;

          if (newCommentAction) {
            setResult((prev) => [...prev, newCommentAction]);

            openSnackbar({
              open: true,
              message: "Comment Action Added Successfully!",
              anchorOrigin: { vertical: "top", horizontal: "right" },
              variant: "alert",
              alert: { color: "success" },
            } as SnackbarProps);
          }
        }

        setSubmitting(false);
        handleClose();
      } catch (error) {
        console.error("Error submitting form:", error);
        setSubmitting(false);
      }
    },
  });

  const {
    errors,
    touched,
    handleSubmit,
    isSubmitting,
    getFieldProps,
    setFieldValue,
  } = formik;

  if (loading)
    return (
      <Box sx={{ p: 5 }}>
        <Stack direction="row" justifyContent="center">
          <CircularWithPath />
        </Stack>
      </Box>
    );
  return (
    <ReusableModal open={open} handleClose={handleClose}>
      <FormikProvider value={formik}>
        <LocalizationProvider>
          <Form autoComplete="off" noValidate onSubmit={handleSubmit}>
            <DialogTitle
              color="primary"
              style={{
                textAlign: "center",
                fontSize: "22px",
                fontWeight: "bold",
                marginBottom: "10px",
              }}
            >
              {commentAction ? (
                <FormattedMessage id="Edit Comment Action" />
              ) : (
                <FormattedMessage id="New Comment Action" />
              )}
            </DialogTitle>
            <Divider />
            <DialogContent sx={{ p: 2.5 }}>
              <Grid container>
                <Grid item xs={12} md={5} mb={1}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="comment-action-value">
                      <FormattedMessage id="English Name" />{" "}
                      <span style={{ color: "red" }}>*</span>
                    </InputLabel>
                    <TextField
                      fullWidth
                      id="comment-action-value"
                      placeholder={intl.formatMessage({
                        id: "Enter english name",
                        defaultMessage: "Enter english name",
                      })}
                      {...getFieldProps("value")}
                      error={Boolean(touched.value && errors.value)}
                      helperText={touched.value && errors.value}
                    />
                  </Stack>
                </Grid>
                <Grid item md={1} />
                <Grid item xs={12} md={5} mb={1}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="comment-action-value-trans">
                      {<FormattedMessage id="Arabic Name" />}{" "}
                      <span style={{ color: "red" }}>*</span>
                    </InputLabel>
                    <TextField
                      fullWidth
                      id="comment-action-value-trans"
                      {...getFieldProps("value_trans")}
                      placeholder={intl.formatMessage({
                        id: "enter-arabic-name",
                        defaultMessage: "Enter arabic name",
                      })}
                      error={Boolean(touched.value_trans && errors.value_trans)}
                      helperText={touched.value_trans && errors.value_trans}
                    />
                  </Stack>
                </Grid>
                <Grid item xs={12} md={12}>
                  <Stack spacing={1}>
                    <InputLabel htmlFor="customer-status">
                      <FormattedMessage id="Status" />
                    </InputLabel>
                    <FormControl fullWidth>
                      <FormControlLabel
                        control={
                          <Switch
                            id="customer-status"
                            checked={formik.values.status}
                            {...getFieldProps("status")}
                            onChange={(event) => {
                              const checked = event.target.checked;
                              setFieldValue("status", checked);
                            }}
                          />
                        }
                        label={
                          formik.values.status ? (
                            <FormattedMessage id="Active" />
                          ) : (
                            <FormattedMessage id="Inactive" />
                          )
                        }
                      />
                    </FormControl>
                    {touched.status && errors.status && (
                      <FormHelperText
                        error
                        id="standard-weight-helper-text-email-login"
                        sx={{ pl: 1.75 }}
                      >
                        {errors.status}
                      </FormHelperText>
                    )}
                  </Stack>
                </Grid>
              </Grid>
            </DialogContent>
            <Divider />
            <DialogActions sx={{ p: 2.5 }}>
              <Grid container justifyContent="center" alignItems="center">
                <Stack direction="row" spacing={2} sx={{ width: 1 }}>
                  <Grid item md={2} />
                  <Grid item md={4}>
                    {commentAction ? (
                      <Button
                        disabled={isSubmitting}
                        fullWidth
                        type="submit"
                        variant="contained"
                      >
                        <FormattedMessage id="Update" />
                      </Button>
                    ) : (
                      <Button
                        disabled={isSubmitting}
                        fullWidth
                        type="submit"
                        variant="contained"
                      >
                        <FormattedMessage id="Submit" />
                      </Button>
                    )}
                  </Grid>
                  <Grid item md={4}>
                    <Button
                      fullWidth
                      color="secondary"
                      variant="outlined"
                      onClick={handleClose}
                    >
                      <FormattedMessage id="Cancel" />
                    </Button>
                  </Grid>
                  <Grid item md={2} />
                </Stack>
              </Grid>
            </DialogActions>
          </Form>
        </LocalizationProvider>
      </FormikProvider>
    </ReusableModal>
  );
};

export default FormCommentActionAdd;
